import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { PasswordInput } from '@/shared/components/ui/password-input';
import { Separator } from '@/shared/components/ui/separator';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/shared/utils/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { IdCard, Lock, Mail, Phone, User } from 'lucide-react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Stack } from '@/shared/components/ui/row';

const registerSchema = z
  .object({
    username: z.string().min(3, '사용자명은 최소 3자 이상이어야 합니다!'),
    email: z.string().email('올바른 이메일 형식이 아닙니다!'),
    fullName: z.string().min(1, '이름을 입력해주세요!'),
    phone: z.string().min(1, '전화번호를 입력해주세요!'),
    password: z.string().min(6, '비밀번호는 최소 6자 이상이어야 합니다!'),
    confirmPassword: z.string().min(1, '비밀번호 확인을 입력해주세요!'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: '비밀번호가 일치하지 않습니다!',
    path: ['confirmPassword'],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;

export const RegisterPage: React.FC = () => {
  const toast = useToast();
  const { isLoading, isAuthenticated, isInitialized, initialize } =
    useAuthStore();
  const navigate = useNavigate();

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      username: '',
      email: '',
      fullName: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (!isInitialized) initialize();
  }, [isInitialized, initialize]);

  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      navigate({ to: ROUTE_PATHS.DASHBOARD } as never);
    }
  }, [isInitialized, isAuthenticated, navigate]);

  /**
   * 어드민 계정은 자가 회원가입이 없다.
   * 관리자가 /admin/admin-users 로 생성한 뒤 email 로그인한다.
   */
  const onSubmit = async (_values: RegisterFormValues) => {
    toast.info(
      '어드민 계정은 관리자가 생성합니다. 이메일로 로그인해 주세요.',
    );
    navigate({ to: ROUTE_PATHS.LOGIN as never });
  };

  return (
    <div className="register-container">
      <Card className="register-card w-full max-w-lg">
        <CardContent className="p-8">
          <div className="register-header mb-8 text-center">
            <img
              src="/rudalink_bi.svg"
              alt="RUDALINK"
              className="mx-auto h-8 w-auto max-w-[200px] object-contain"
            />
            <p className="mt-3 text-sm text-muted-foreground">
              새로운 관리자 계정을 생성하세요
            </p>
          </div>

          <Form {...form}>
            <Stack as="form" gap={16} onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>사용자명</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          size="sm"
                          startIcon={<User />}
                          placeholder="사용자명"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>이메일</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          size="sm"
                          startIcon={<Mail />}
                          placeholder="이메일"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>이름</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          size="sm"
                          startIcon={<IdCard />}
                          placeholder="이름"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>전화번호</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          size="sm"
                          startIcon={<Phone />}
                          placeholder="전화번호"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>비밀번호</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        size="sm"
                        startIcon={<Lock />}
                        placeholder="비밀번호"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>비밀번호 확인</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        size="sm"
                        startIcon={<Lock />}
                        placeholder="비밀번호 확인"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" loading={isLoading}>
                회원가입
              </Button>
            </Stack>
          </Form>

          <Separator className="my-6" />

          <p className="text-center text-sm text-muted-foreground">
            이미 계정이 있으신가요?{' '}
            <Link to="/" className="font-medium text-ruda-primary hover:text-ruda-primary-light">
              로그인하기
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
